import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import Layout from './components/common/Layout';
import Dashboard from './components/dashboard/Dashboard';
import ProviderList from './components/providers/ProviderList';
import ProviderForm from './components/providers/ProviderForm';
import ProviderDetail from './components/providers/ProviderDetail';
import QuoteComparison from './components/quotes/QuoteComparison';
import ShipmentList from './components/shipments/ShipmentList';
import ShipmentForm from './components/shipments/ShipmentForm';
import ShipmentDetail from './components/shipments/ShipmentDetail';
import TaskList from './components/tasks/TaskList';
import ShipmentTracking from './components/tracking/ShipmentTracking';
import AnalyticsDashboard from './components/analytics/AnalyticsDashboard';
import QuoteExplorer from './components/analytics/QuoteExplorer';
import TruckingParameters from './components/admin/TruckingParameters';
import DutyRates from './components/admin/DutyRates';
import ForwarderRates from './components/admin/ForwarderRates';
import CustomerDrayage from './components/admin/CustomerDrayage';
import UsCustomsConfig from './components/admin/UsCustomsConfig';
import EstimateCalculator from './components/estimates/EstimateCalculator';
import ManufacturerQuoteSettings from './components/manufacturers/ManufacturerQuoteSettings';
import HsCodeManager from './components/manufacturers/HsCodeManager';
import FuelCostAdmin from './components/fuelCosts/FuelCostAdmin';
import WritebackDashboard from './components/writeback/WritebackDashboard';
import TestDataSetup from './components/admin/TestDataSetup';
import EstimateConfigAdmin from './components/admin/EstimateConfigAdmin';
import PortsAdmin from './components/admin/PortsAdmin';
import AIObservability from './components/admin/AIObservability';
import StandaloneEstimator from './components/estimates/StandaloneEstimator';
import AdminHub from './components/admin/AdminHub';
import TenantsList from './components/admin/TenantsList';
import TenantDetail from './components/admin/TenantDetail';
import PromptsManagement from './components/admin/PromptsManagement';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import V2App from './components/v2/V2App';
import V2WorkspacePreview from './components/v2/workspace/V2WorkspacePreview';
import ShipmentsGridPreview from './components/v2/workspace/preview/ShipmentsGridPreview';
import './App.css';

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// The public preview routes render components that call Clerk's useUser
// (NavSidebar, ActivityLog) for the signed-in avatar. They require no auth, but
// those hooks still need a ClerkProvider ancestor or they throw. ClerkProvider
// on its own does NOT force sign-in (no SignedIn/RedirectToSignIn here) —
// useUser just resolves to a signed-out context. ClerkProvider lives inside
// V2App for the rest of the app, which is why these routes — mounted outside
// V2App — need their own. (See critical-considerations 2026-06-04.)
function PublicPreview({ children }) {
  if (!CLERK_PUBLISHABLE_KEY) return children;
  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
      {children}
    </ClerkProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Workspace + shipments-grid preview — public, no auth (Clerk context only) */}
          <Route path="/preview/shipments" element={<PublicPreview><ShipmentsGridPreview /></PublicPreview>} />
          <Route path="/preview/workspace" element={<PublicPreview><V2WorkspacePreview /></PublicPreview>} />

          {/* V1 Admin auth routes */}
          <Route path="/admin/login" element={<LoginPage />} />
          <Route path="/admin/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/admin/reset-password" element={<ResetPasswordPage />} />

          {/* V1 Admin panel — protected, under /admin/* */}
          <Route element={<ProtectedRoute />}>
            <Route path="/admin" element={<Layout />}>
              <Route index element={<Dashboard />} />

              <Route path="providers" element={<ProviderList />} />
              <Route path="providers/new" element={<ProviderForm />} />
              <Route path="providers/:id" element={<ProviderDetail />} />
              <Route path="providers/:id/edit" element={<ProviderForm isEdit={true} />} />

              <Route path="shipments" element={<ShipmentList />} />
              <Route path="shipments/new" element={<ShipmentForm />} />
              <Route path="shipments/:id" element={<ShipmentDetail />} />
              <Route path="shipments/:id/tracking" element={<ShipmentTracking />} />

              <Route path="shipments/:id/quotes/compare" element={<QuoteComparison />} />
              <Route path="comparisons/:comparisonId" element={<QuoteComparison />} />

              <Route path="tasks" element={<TaskList />} />
              <Route path="analytics" element={<AnalyticsDashboard />} />
              <Route path="quote-explorer" element={<QuoteExplorer />} />

              <Route path="tenants" element={<TenantsList />} />
              <Route path="tenants/:id" element={<TenantDetail />} />
              <Route path="prompts" element={<PromptsManagement />} />
              <Route path="trucking-parameters" element={<TruckingParameters />} />
              <Route path="duty-rates" element={<DutyRates />} />
              <Route path="forwarder-rates" element={<ForwarderRates />} />
              <Route path="customer-drayage" element={<CustomerDrayage />} />
              <Route path="us-customs" element={<UsCustomsConfig />} />
              <Route path="manufacturers/:id/settings" element={<ManufacturerQuoteSettings />} />
              <Route path="hs-codes" element={<HsCodeManager />} />
              <Route path="fuel-costs" element={<FuelCostAdmin />} />
              <Route path="writeback" element={<WritebackDashboard />} />
              <Route path="setup" element={<TestDataSetup />} />
              <Route path="estimate-calculator" element={<StandaloneEstimator />} />
              <Route path="estimate-config" element={<EstimateConfigAdmin />} />
              <Route path="ports" element={<PortsAdmin />} />
              <Route path="ai-observability" element={<AIObservability />} />
              <Route path="hub" element={<AdminHub />} />

              <Route path="estimates/:shipmentId" element={<EstimateCalculator />} />
            </Route>
          </Route>

          {/* User app (V2 / Clerk auth) — at root /* */}
          <Route path="/*" element={<V2App />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

const NotFound = () => (
  <div className="not-found">
    <h1>404</h1>
    <p>Page not found</p>
    <a href="/admin">Go back to admin dashboard</a>
  </div>
);

export default App;
